import { Table } from '@hotjar/hotsauce';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import type {
  LoginEvent,
  LoginEventProviderId,
  LoginEventReason,
} from '../../domains/profile/types';

const EMPTY_VALUE_PLACEHOLDER = '-';

const PROVIDER_ID_MAPPING: Record<NonNullable<LoginEventProviderId>, string> = {
  enterprise: 'Enterprise SSO',
  google: 'Google',
  userpass: 'password',
};

const EVENT_REASON_MAPPING: Record<NonNullable<LoginEventReason>, string> = {
  'mfa-otp-verify': 'MFA OTP',
  'mfa-recovery-code-verified': 'MFA Recovery Code',
  'mfa-device-key': 'Trusted device',
};

function getLocationDisplay(countryName: undefined | string, cityName: undefined | string): string {
  if (countryName === '' || countryName === undefined) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  if (cityName === '' || cityName === undefined) {
    return countryName;
  }

  return `${countryName} (${cityName})`;
}

function getSessionTypeDisplay(
  providerId: undefined | LoginEventProviderId,
  eventReason: undefined | LoginEventReason,
): string {
  let sessionType = 'password';
  if (providerId != null && providerId !== '') {
    sessionType = PROVIDER_ID_MAPPING[providerId] ?? providerId;
  }

  if (eventReason != null && eventReason !== '') {
    sessionType += ` (${EVENT_REASON_MAPPING[eventReason] ?? eventReason})`;
  }

  return sessionType;
}

interface LoginEventTableRowProps {
  loginEvent: LoginEvent;
}

function getTimeDisplay(timestamp: string) {
  if (!timestamp) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  return `${formatDistanceToNowStrict(parseISO(timestamp))} ago`;
}

export const LoginEventTableRow = ({ loginEvent }: LoginEventTableRowProps): JSX.Element => {
  const { timestamp, provider, client, event } = loginEvent;

  return (
    <Table.Row>
      <Table.Cell>Signed in with {getSessionTypeDisplay(provider?.id, event?.reason)}</Table.Cell>
      <Table.Cell>{getLocationDisplay(client?.geo?.countryName, client?.geo?.cityName)}</Table.Cell>
      <Table.Cell>{getTimeDisplay(timestamp)}</Table.Cell>
    </Table.Row>
  );
};
